import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, CircularProgress, 
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Button, Modal, List, ListItem, ListItemText, Divider, Grid, Avatar
} from '@mui/material';
import { useUser } from '../../Context/UserContext';
import { GetApi } from '../../Services/api/ApiCall';
import API_URLS from "../../Services/api/action";
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PieChartIcon from '@mui/icons-material/PieChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import { GaugeContainer, GaugeReferenceArc, GaugeValueArc } from '@mui/x-charts/Gauge';

import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, PieChart, Pie, Cell, Sector 
} from 'recharts';

import { useNavigate } from 'react-router-dom';
import './ProductionCenterDashboard.css';

// --- Helpers ---
const getMonthName = (monthNum) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[monthNum - 1] || '';
};
 
const RADIAN = Math.PI / 180;
const formatDate = (dateStr) => {
  if (!dateStr) return 'Not set';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PIE_COLORS = ['#1b5e20', '#2e7d32', '#00695c', '#43a047', '#558b2f', '#7cb342', '#aed581'];

// --- COMPONENT ---
const ProductionCenterDashboard = () => {
  const { authData, token } = useUser();
  const navigate = useNavigate();

  // States
  const [loading, setLoading] = useState(true);
  const [countsData, setCountsData] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [saplingsData, setSaplingsData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null); // For Pie Hover
  
  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState(null);

  const centerId = authData?.production_center_id || 14;

  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        const countUrl = `${API_URLS.GetproductionCnt}?production_center_id=${centerId}`;
        const salesUrl = `${API_URLS.GetSalesurl}?production_center_id=${centerId}`;
        const pendingUrl = `${API_URLS.GetpendingUrl}?production_center_id=${centerId}&status=pending`;
        const saplingsUrl = `${API_URLS.saplingsUrl}?production_center_id=${centerId}`;

        const [countRes, salesRes, pendingRes, saplingsRes] = await Promise.all([
          GetApi(countUrl, token),
          GetApi(salesUrl, token),
          GetApi(pendingUrl, token),
          GetApi(saplingsUrl, token)
        ]);

        if (countRes?.data && countRes.data.length > 0) {
          setCountsData(countRes.data[0]);
        }

        const salesMap = new Map();
        (salesRes?.data || []).forEach(item => {
          salesMap.set(item.month, {
            sales: item.total_selled,
            price: item.total_selled_price
          });
        });
        
        const fullYearData = Array.from({ length: 12 }, (_, i) => ({
          name: getMonthName(i + 1),
          sales: salesMap.get(i + 1)?.sales || 0,
          price: salesMap.get(i + 1)?.price || 0
        }));
        setSalesData(fullYearData);

        setPendingRequests(pendingRes?.results || []);

        if (saplingsRes?.success && saplingsRes.data) {
          const aggregated = saplingsRes.data.reduce((acc, curr) => {
            const name = curr.name || 'Unknown';
            const existing = acc.find(item => item.name === name);
            
            if (existing) {
              existing.value += curr.saplings_available;
              existing.total_selled += (curr.total_selled || 0);
            } else {
              acc.push({ 
                name: name, 
                value: curr.saplings_available,
                total_selled: curr.total_selled || 0
              });
            }
            return acc;
          }, []);
          setSaplingsData(aggregated);
        }

      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [token, centerId]);

  // --- HANDLERS ---
  const handleViewDetails = (farmer) => {
    setSelectedFarmer(farmer);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedFarmer(null);
  };

  // --- Target Chart Logic ---
  const targetValue = countsData?.target_quantity || 0;

  const currentStockCount = saplingsData.reduce((sum, item) => {
    const value = item.value || 0;
    const sold = item.total_selled || 0;
    return sum + (value + sold);
  }, 0);
  
  const angle = targetValue > 0 
    ? -110 + (currentStockCount / targetValue) * 220 
    : -110;

  // ✅ UPDATED: Long Bended Line for Hover
  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    
    const expandedRadius = outerRadius + 10;
    const cos = Math.cos(-RADIAN * midAngle);
    const sin = Math.sin(-RADIAN * midAngle);
    
    // 1. Start point (just outside the slice)
    const sx = cx + (expandedRadius + 5) * cos;
    const sy = cy + (expandedRadius + 5) * sin;
    
    // 2. Elbow point (longer radial line outwards to create the bend far away)
    const elbowRadius = expandedRadius + 45; // Increased length to push text away
    const mx = cx + elbowRadius * cos;
    const my = cy + elbowRadius * sin;
    
    // 3. End point (strictly horizontal line extending far out)
    const horizontalLength = 70; // Very long horizontal line to avoid near text
    const ex = mx + (cos >= 0 ? horizontalLength : -horizontalLength);
    const ey = my; 
    
    const textAnchor = cos >= 0 ? 'start' : 'end';
    const textOffset = cos >= 0 ? 10 : -10;

    return (
      <g>
        {/* Expanded slice */}
        <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={expandedRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />
        {/* Subtle outer glow */}
        <Sector cx={cx} cy={cy} startAngle={startAngle} endAngle={endAngle} innerRadius={expandedRadius + 4} outerRadius={expandedRadius + 8} fill={fill} opacity={0.3} />
        
        {/* The Exact Bended Line (Elbow Connector) */}
        <polyline 
          points={`${sx},${sy} ${mx},${my} ${ex},${ey}`} 
          stroke={fill} 
          fill="none" 
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Dot at the end of the line */}
        <circle cx={ex} cy={ey} r={3} fill={fill} stroke="#fff" strokeWidth={1.5} />
        
        {/* Name text */}
        <text x={ex + textOffset} y={ey - 6} textAnchor={textAnchor} fill="#222" fontSize={14} fontWeight={700} dominantBaseline="central">
          {payload.name}
        </text>
        
        {/* Value & Percentage text */}
        <text x={ex + textOffset} y={ey + 14} textAnchor={textAnchor} fill="#555" fontSize={12} dominantBaseline="central">
          {`${value} units (${(percent * 100).toFixed(1)}%)`}
        </text>
      </g>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="prod-dashboard-container">
      
      {/* --- ROW 1: Targets & Sales Graph --- */}
      <div className="dashboard-row">
        
        {/* DIV 1: Target & Counts */}
        <div className="card-chart">
          <Box sx={{ display: 'flex', gap: 2, height: 'auto', width: '100%', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            
            {/* LEFT: Target Card */}
            <Paper sx={{ width: { xs: '100%', md: 'calc(70% - 8px)' }, minHeight: { xs: 280, md: 300 }, background: 'linear-gradient(135deg, #ffffff 0%, #f1f8e9 100%)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRadius: 3, boxShadow: 3, p: 3, position: 'relative', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <Box sx={{ position: 'absolute', top: -20, right: -20, width: 150, height: 150, borderRadius: '50%', background: 'radial-gradient(circle, rgba(27,94,32,0.1) 0%, rgba(255,255,255,0) 70%)' }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1 }}>
                <Box>
                  <Typography variant="h6" fontWeight={800} color="#1b5e20" sx={{ mt: 0.5 }}>PRODUCTION TARGET GOAL</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#1b5e20', width: 50, height: 50, boxShadow: 3 }}>
                  <TrendingUpIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>

              <Box sx={{ width: '100%', height: 220, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <GaugeContainer width={220} height={220} startAngle={-110} endAngle={110} value={currentStockCount} valueMax={targetValue || 1} sx={{ [`& .MuiGauge-valueArc`]: { fill: '#1b5e20' }, [`& .MuiGauge-referenceArc`]: { fill: '#e0e0e0' } }}>
                  <GaugeReferenceArc />
                  <GaugeValueArc />
                  <svg width="220" height="220" style={{ position: 'absolute', top: "2%", left: 0, pointerEvents: 'none' }}>
                    <g transform={`translate(110, 110) rotate(${angle})`}>
                      <path d="M-4 0 L0 -85 L4 0 Z" fill="#d32f2f" stroke="#b71c1c" strokeWidth="1" strokeLinejoin="round" />
                      <circle cx="0" cy="0" r="8" fill="#d32f2f" />
                    </g>
                  </svg>
                </GaugeContainer>

                <Box sx={{ position: 'absolute', top: '75%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', zIndex: 2 }}>
                  <Typography variant="h5" fontWeight={600} color="#1b5e20" sx={{ lineHeight: 1, textShadow: '0px 2px 4px rgba(0,0,0,0.05)' }}>
                    {currentStockCount}/{targetValue}
                  </Typography>
                  <Typography variant="body1" fontWeight={700} color="#666" sx={{ mt: 1 }}>TARGET</Typography>
                </Box>
              </Box>

              <Box sx={{ borderTop: '1px dashed #e0e0e0', pt: 2, zIndex: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, color: "#1b5e20" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Start</Typography>
                        <Typography variant="body2" fontWeight={700}>{formatDate(countsData?.start_date)}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, color: "#d32f2f" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">End</Typography>
                        <Typography variant="body2" fontWeight={700}>{formatDate(countsData?.end_date)}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                     <Divider sx={{ my: 0.5 }} />
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <PersonIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="caption" color="text.secondary">
                          Created by: <b style={{ color: '#333' }}>{countsData?.created_by_name || 'System'}</b>
                        </Typography>
                      </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* RIGHT: 3 Stacked Cards */}
            <Box sx={{ width: { xs: '100%', md: 'calc(30% - 8px)' }, display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 2, height: { xs: 'auto', md: '100%' } }}>
              <Paper sx={{ flex: 1, p: 2, borderRadius: 3, boxShadow: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', borderLeft: '5px solid #1976d2', bgcolor: 'white' }}>
                <Typography color="text.secondary" variant="caption" textTransform="uppercase">Order Placed</Typography>
                <Typography variant="h4" fontWeight={800} color="#1976d2">{countsData?.order_placed_count || 0}</Typography>
                <TrendingUpIcon sx={{ fontSize: 20, color: '#1976d2', opacity: 0.5, mt: 1 }} />
              </Paper>
              <Paper sx={{ flex: 1, p: 2, borderRadius: 3, boxShadow: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', borderLeft: '5px solid #2e7d32', bgcolor: 'white' }}>
                <Typography color="text.secondary" variant="caption" textTransform="uppercase">Billed Count</Typography>
                <Typography variant="h4" fontWeight={800} color="#2e7d32">{countsData?.billed_count || 0}</Typography>
                <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#2e7d32', opacity: 0.5, mt: 1 }} />
              </Paper>
              <Paper sx={{ flex: 1, p: 2, borderRadius: 3, boxShadow: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', borderLeft: '5px solid #ff9800', bgcolor: 'white' }}>
                <Typography color="text.secondary" variant="caption" textTransform="uppercase">Pending</Typography>
                <Typography variant="h4" fontWeight={800} color="#ff9800">{countsData?.pending_count || 0}</Typography>
                <AccessTimeIcon sx={{ fontSize: 20, color: '#ff9800', opacity: 0.5, mt: 1 }} />
              </Paper>
            </Box>
          </Box>
        </div>

        {/* DIV 2: Line Graph */}
        <div className="card-chart">
          <Box sx={{ pt: '3px', pl:'1px', pb:'2px', borderRadius: 3, height: { xs: 'auto', md: '100%' }, minHeight: { xs: 300, md: 300 }, display: 'flex', flexDirection: 'column', bgcolor: "white", boxShadow: 1 }}>
            <Typography variant="h6" fontWeight={700} mb={2} color="#1b5e20">Monthly Sales & Revenue</Typography>
            <Box sx={{ width: '100%', height: { xs: 250, md: '100%' } }}>
              <ResponsiveContainer>
                <LineChart data={salesData}>
                  <CartesianGrid vertical={true} horizontal={true} stroke="#e0e0e0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} orientation="left" stroke="#43a047" />
                  <YAxis yAxisId="right" tick={{ fontSize: 11 }} orientation="right" stroke="#8884d8" />
                  <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', bgcolor: '#fff' }} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="sales" stroke="#43a047" strokeWidth={3} dot={{ fill: '#43a047', r: 4 }} activeDot={{ r: 6 }} name="Saplings distributed" />
                  <Line yAxisId="right" type="monotone" dataKey="price" stroke="#8884d8" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#8884d8', r: 3 }} activeDot={{ r: 5 }} name="Revenue (₹)" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </div>
      </div>

      {/* --- ROW 2: Pending Requests & Pie Charts --- */}
      <div className="dashboard-row">

        {/* DIV 4: Saplings Pie Chart */}
        <div className="card-chart">
          <Box sx={{ pb:'6px', borderRadius: 3, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', bgcolor: '#fff', boxShadow: 1 }}>
            <Typography variant="h6" fontWeight={700} mb={2} color="#1b5e20">
              <PieChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Saplings Distribution
            </Typography>
            
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              {saplingsData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography variant="body2" color="text.secondary">No saplings data available</Typography>
                </Box>
              ) : (
                <>
                  {/* ✅ Container updated to give space for long lines */}
                  <Box sx={{ width: '100%', height: { xs: 280, md: 320 }, maxWidth: 550 }}>
                    <ResponsiveContainer>
                      {/* ✅ Margin added so lines don't get cut off */}
                      <PieChart margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
                        <Pie
                          activeIndex={activeIndex}
                          activeShape={renderActiveShape}
                          data={saplingsData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                          onMouseEnter={(_, index) => setActiveIndex(index)}
                          onMouseLeave={() => setActiveIndex(null)}
                        >
                          {saplingsData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 2, px: 2 }}>
                    {saplingsData.map((entry, index) => (
                      <Chip key={index} label={`${entry.name} (${entry.value})`} size="small" sx={{ fontSize: '11px', bgcolor: PIE_COLORS[index % PIE_COLORS.length], color: '#fff', height: 24, fontWeight: 600 }} />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </div>

        {/* DIV 3: Pending Farmer Requests - FIXED TABLE */}
        <div className="card-chart">
          <Box sx={{ height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', bgcolor: "white", boxShadow: 1, borderRadius: 3, overflow: 'hidden' }}>
            {/* Header */}
            <Box className="farmer-req-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, flexShrink: 0, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 0 } }}>
              <Typography variant="h6" fontWeight={700} color="#1b5e20" sx={{ whiteSpace: 'nowrap' }}>Pending Farmer Requests</Typography>
              <Button variant="contained" size="small" onClick={() => navigate('/allocations')} sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1b5e20', '&:hover': { bgcolor: '#333' }, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>See All</Button>
            </Box>

            {/* Table Container */}
            <TableContainer sx={{ flex: 1, overflowX: 'auto', overflowY: 'auto', px: 2, pb: 2 }}>
              <Table stickyHeader size="small" sx={{ minWidth: 0, tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: '#fff', bgcolor: '#1b5e20', width: '45%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Farmer Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#fff', bgcolor: '#1b5e20', width: '30%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#fff', bgcolor: '#1b5e20', width: '25%', textAlign: 'center' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 5, color: '#aaa' }}>No pending requests</TableCell>
                    </TableRow>
                  ) : (
                    pendingRequests.map((row) => (
                      <TableRow key={row.request_id} hover sx={{ '&:hover': { bgcolor: '#f1f8e9' } }}>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', py: 1.5 }} title={row.farmer_name}>{row.farmer_name}</TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', py: 1.5 }} title={row.farmer_mobile}>{row.farmer_mobile}</TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <IconButton color="primary" onClick={() => handleViewDetails(row)} sx={{ p: 0.5 }}>
                            <VisibilityIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </div>
      </div>

      {/* MODAL FOR VIEWING ORDER DETAILS */}
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: { xs: '90%', sm: 400 }, bgcolor: 'background.paper', borderRadius: 4, boxShadow: 24, p: 4, outline: 'none' }}>
          <Typography variant="h6" fontWeight={700} mb={2} color="#1b5e20">Order Details: {selectedFarmer?.orderid}</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>Farmer: {selectedFarmer?.farmer_name} ({selectedFarmer?.farmer_code})</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1}>Requested Items:</Typography>
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {selectedFarmer?.requested_items?.map((item, idx) => (
              <ListItem key={idx} sx={{ bgcolor: '#f9f9f9', mb: 1, borderRadius: 1 }}>
                <ListItemText primary={item.species_name || 'Unknown Species'} secondary={`Qty: ${item.requested_quantity} | Status: ${item.item_status}`} />
              </ListItem>
            ))}
          </List>
          <Button fullWidth variant="contained" sx={{ mt: 2, bgcolor: '#1b5e20' }} onClick={handleCloseModal}>Close</Button>
        </Box>
      </Modal>

      {/* CLEAN RESPONSIVE CSS */}
      <style>{`
        .dashboard-row {
          display: flex;
          gap: 16px;
        }
        .card-chart {
          flex: 1;
          min-width: 0;
        }
        @media (max-width: 900px) {
          .dashboard-row {
            flex-direction: column;
          }
        }
      `}</style>
    </Box>
  );
};

export default ProductionCenterDashboard;